import { isCallTask } from "Components/shared/AgentsDropdown/helpers";
import { mapReservationCreatedSSEToRVTask } from "Pages/agentDesktop/Pages/AgentDesktopTasks/Shared/Utils/Utils";
import { getLogger } from "Services/LoggingService";
import { addEventEmitterListener } from "Services/server-sent-events/EventEmitter";

import { selectAgentInformation } from "../agent/AgentInformationSlice";
import { selectConference } from "../conferences";
import { selectConferenceSnapshotActiveParticipants } from "../conferences/ConferenceSnapshotSelectors";
import { reduxStore } from "../Storage";
import {
    handleReservationAccepted,
    handleReservationCreated,
    backfillExistingActiveTasks,
    handleReservationCanceled,
    handleReservationCompleted,
    handleReservationRejected,
    handleReservationRescinded,
    handleReservationTimedOut,
    handleReservationWrapup,
    handleTaskUpdate,
} from "./Thunks";

import type { MessageType, ReservationCreatedSSETaskData } from "@regal-voice/shared-types";
import type { Device } from "@twilio/voice-sdk";
import type { FlagTypes } from "Application/thirdParty/launchDarkly";

const logger = getLogger("AutoAccept Calls");

export function dispatchHandleReservationCreated(
    data: MessageType["twilio"]["reservation.created"],
    { blockAutoAcceptedCallsWhenAgentIsOnActiveCall, browserNotifications }: Partial<FlagTypes>,
    device: Device | undefined,
    fetchNewDeviceToken: () => Promise<void>
): void {
    const rvTask = mapReservationCreatedSSEToRVTask(data.task as ReservationCreatedSSETaskData);
    if (blockAutoAcceptedCallsWhenAgentIsOnActiveCall && isCallTask({ task: rvTask }) && rvTask.attributes.autoAnswer) {
        const state = reduxStore.getState();
        const activeConference = selectConference(state);

        const activeParticipants = selectConferenceSnapshotActiveParticipants(state);

        const agent = selectAgentInformation(state);
        // if any active participant is has started a call
        const callStarted = activeParticipants.some(
            (participant) => participant.status === "ringing" || participant.status === "initiated"
        );

        if (
            (activeConference?.connected || callStarted) && // if there is an active call
            !agent.isAiAgent // and the agent is not an AI agent
        ) {
            // then prevent this new task from interrupting the active one
            rvTask.attributes.autoAnswer = false;
            logger.warn(`Task was not auto-accepted because a conference is already in progress`, {
                task: rvTask,
                activeConference,
                activeParticipants,
                agent,
            });
        }
    }

    reduxStore.dispatch(
        handleReservationCreated({
            task: rvTask,
            flags: { browserNotifications },
            device,
            fetchNewDeviceToken,
        })
    );
}

function dispatchHandleReservationAccepted(
    {
        reservation: { sid: reservationSid },
        task: { reservationAcceptedAt } = {},
    }: MessageType["twilio"]["reservation.accepted"],
    device: Device | undefined,
    fetchNewDeviceToken: () => Promise<void>
) {
    reduxStore.dispatch(handleReservationAccepted(device, fetchNewDeviceToken, reservationSid, reservationAcceptedAt));
}

function dispatchHandleReservationCanceled({
    reservation: { sid: reservationSid },
    task,
}: MessageType["twilio"]["reservation.canceled"]) {
    reduxStore.dispatch(handleReservationCanceled(reservationSid, task));
}

function dispatchHandleReservationCompleted({
    reservation: { sid: reservationSid },
}: MessageType["twilio"]["reservation.completed"]) {
    reduxStore.dispatch(handleReservationCompleted(reservationSid));
}

function dispatchHandleReservationRejected({
    reservation: { sid: reservationSid },
}: MessageType["twilio"]["reservation.rejected"]) {
    reduxStore.dispatch(handleReservationRejected(reservationSid));
}

function dispatchHandleReservationRescinded({
    reservation: { sid: reservationSid },
}: MessageType["twilio"]["reservation.rescinded"]) {
    reduxStore.dispatch(handleReservationRescinded(reservationSid));
}

function dispatchHandleReservationTimedOut({
    reservation: { sid: reservationSid },
}: MessageType["twilio"]["reservation.timeout"]) {
    reduxStore.dispatch(handleReservationTimedOut(reservationSid));
}

function dispatchHandleReservationWrapup({
    task: { reservationUpdatedDate },
    reservation: { sid: reservationSid },
}: MessageType["twilio"]["reservation.wrapup"]) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    reduxStore.dispatch(handleReservationWrapup({ reservationSid, reservationUpdatedDate: reservationUpdatedDate! }));
}

function dispatchHandleTaskUpdate({ task }: MessageType["twilio"]["task.updated"]) {
    reduxStore.dispatch(handleTaskUpdate(task));
}

type SseListenerProps = {
    flags: {
        blockAutoAcceptedCallsWhenAgentIsOnActiveCall?: boolean;
        browserNotifications?: number;
    };
    device: Device | undefined;
    fetchNewDeviceToken: () => Promise<void>;
};

/**
 * Sets up listeners for all task SSEs and returns a function to unsubscribe them
 */
export function subscribeToTaskSSE({
    flags: { blockAutoAcceptedCallsWhenAgentIsOnActiveCall, browserNotifications },
    device,
    fetchNewDeviceToken,
}: SseListenerProps): () => void {
    const dispatch = reduxStore.dispatch;

    dispatch(backfillExistingActiveTasks({}));

    const unsubscribers: Array<() => void> = [];

    unsubscribers.push(
        addEventEmitterListener("twilio", "reservation.created", (data: MessageType["twilio"]["reservation.created"]) =>
            dispatchHandleReservationCreated(
                data,
                { blockAutoAcceptedCallsWhenAgentIsOnActiveCall, browserNotifications },
                device,
                fetchNewDeviceToken
            )
        )
    );

    unsubscribers.push(
        addEventEmitterListener(
            "twilio",
            "reservation.accepted",
            (data: MessageType["twilio"]["reservation.accepted"]) =>
                dispatchHandleReservationAccepted(data, device, fetchNewDeviceToken)
        )
    );
    unsubscribers.push(addEventEmitterListener("twilio", "reservation.canceled", dispatchHandleReservationCanceled));

    unsubscribers.push(addEventEmitterListener("twilio", "reservation.completed", dispatchHandleReservationCompleted));

    unsubscribers.push(addEventEmitterListener("twilio", "reservation.rejected", dispatchHandleReservationRejected));

    unsubscribers.push(addEventEmitterListener("twilio", "reservation.rescinded", dispatchHandleReservationRescinded));

    unsubscribers.push(addEventEmitterListener("twilio", "reservation.timeout", dispatchHandleReservationTimedOut));

    unsubscribers.push(addEventEmitterListener("twilio", "reservation.wrapup", dispatchHandleReservationWrapup));

    unsubscribers.push(addEventEmitterListener("twilio", "task.updated", dispatchHandleTaskUpdate));

    return () => {
        unsubscribers.forEach((unsubscriber) => unsubscriber());
    };
}
